import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  selectedUser: null,

  allUsers: {
    isLoading: true,
    data: [],
  },

  userListing: {
    isLoading: true,
    data: [],
  },

  channelMessages: {
    isLoading: true,
    data: null,
    subscription: null,
  },

  reactionUsers: {},
  optimisticMessages: {},
  pendingReactions: {},
};
export const chatsSlice = createSlice({
  name: "chats",
  initialState,

  reducers: {
    resetChatData: () => {
      return initialState;
    },
    setIsAllUsersLoading: (state, action) => {
      state.allUsers.isLoading = action.payload;
    },
    setAllUsers: (state, action) => {
      state.allUsers.data = action.payload;
    },

    setIsChatUsersLoading: (state, action) => {
      state.userListing.isLoading = action.payload;
    },
    setChatUsers: (state, action) => {
      state.userListing.data = action.payload;
    },

    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },

    setIsChannelMessagesLoading: (state, action) => {
      state.channelMessages.isLoading = action.payload;
    },
    setChannelMessages: (state, action) => {
      state.channelMessages.data = action.payload;
    },

    setReactionUsers: (state, action) => {
      const { messageId, emoji, users } = action.payload;
      if (!state.reactionUsers[messageId]) {
        state.reactionUsers[messageId] = {};
      }
      state.reactionUsers[messageId][emoji] = users;
    },

    setOptimisticMessage: (state, action) => {
      const { messageId, message } = action.payload;
      state.optimisticMessages[messageId] = message;
    },

    clearOptimisticMessage: (state, action) => {
      const { messageId } = action.payload;
      delete state.optimisticMessages[messageId];
    },

    setPendingReaction: (state, action) => {
      const { messageId, emoji, userId, isAdding } = action.payload;
      if (!state.pendingReactions[messageId]) {
        state.pendingReactions[messageId] = {};
      }
      if (!state.pendingReactions[messageId][emoji]) {
        state.pendingReactions[messageId][emoji] = {};
      }
      state.pendingReactions[messageId][emoji][userId] = isAdding;
    },

    clearPendingReaction: (state, action) => {
      const { messageId, emoji, userId } = action.payload;
      if (state.pendingReactions[messageId]?.[emoji]) {
        delete state.pendingReactions[messageId][emoji][userId];
        if (
          Object.keys(state.pendingReactions[messageId][emoji]).length === 0
        ) {
          delete state.pendingReactions[messageId][emoji];
        }
        if (Object.keys(state.pendingReactions[messageId]).length === 0) {
          delete state.pendingReactions[messageId];
        }
      }
    },
  },
});

export const {
  setIsAllUsersLoading,
  setAllUsers,
  setIsChatUsersLoading,
  setChatUsers,
  setSelectedUser,
  setIsChannelMessagesLoading,
  setChannelMessages,
  resetChatData,
  setReactionUsers,
  setOptimisticMessage,
  clearOptimisticMessage,
  setPendingReaction,
  clearPendingReaction,
} = chatsSlice.actions;

export const selectChannelMessages = (state) => state.chats.channelMessages;
export const selectOptimisticMessages = (state) =>
  state.chats.optimisticMessages;
export const selectPendingReactions = (state) => state.chats.pendingReactions;

export const selectOptimisticChannelMessages = createSelector(
  [selectChannelMessages, selectPendingReactions],
  (channelMessages, pendingReactions) => {
    if (!channelMessages.data || !Array.isArray(channelMessages.data)) {
      return channelMessages;
    }

    const optimisticData = channelMessages.data.map((message) => {
      const messagePending = pendingReactions[message.thread_id];
      if (!messagePending) {
        return message;
      }

      const updatedReactions = { ...message.reactions };

      Object.keys(messagePending).forEach((emoji) => {
        const emojiPending = messagePending[emoji];
        if (!emojiPending) return;

        if (!updatedReactions[emoji]) {
          updatedReactions[emoji] = { count: 0, users: {} };
        }

        const reactionData = { ...updatedReactions[emoji] };
        const users = { ...reactionData.users };
        let count = reactionData.count || 0;

        Object.keys(emojiPending).forEach((userId) => {
          const isAdding = emojiPending[userId];
          if (isAdding === true) {
            if (!users[userId]) {
              users[userId] = true;
              count++;
            }
          } else if (isAdding === false) {
            if (users[userId]) {
              delete users[userId];
              count--;
            }
          }
        });

        if (count > 0) {
          updatedReactions[emoji] = { count, users };
        } else {
          delete updatedReactions[emoji];
        }
      });

      return {
        ...message,
        reactions: updatedReactions,
      };
    });

    return {
      ...channelMessages,
      data: optimisticData,
    };
  },
);

export default chatsSlice.reducer;
